import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { LuChartPie } from 'react-icons/lu';
import * as Yup from 'yup';

import { FeatureFlag } from '@ndvr/auth-api';
import { Allocation, AllocationType, Strategy } from '@ndvr/portfolio-api';

import { FormInput } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormLabel } from '@/components/ui/form';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';

import { calculateTotalWeight } from '../../shared-utils';
import { createFormSchema } from './allocation-form-schema';
import {
    AddStrategySection,
    AllocationGroupNameField,
    AllocationTypeSelector,
    StrategyAllocationItem,
} from './components';
import { useStrategyAllocationManager, useStrategyFilter } from './hooks';
import { StrategyTabKey } from './hooks/useStrategyFilter';
import { getDefaultFormValues, getStrategyByGUID } from './utils/allocation-utils';

export type FormValues = Yup.InferType<ReturnType<typeof createFormSchema>>;

export interface EditAllocationDialogProps {
    allocation?: Allocation;
    onSave: (data: FormValues) => void;
    onCancel: () => void;
    open: boolean;
    isSaving?: boolean;
    canCreateSharedAllocation?: boolean;
    existingAllocationNames?: string[];
    existingAllocationGroupNames?: Array<{ name: string; allocationType: AllocationType }>;
    availableStrategies: Strategy[];
}

export function EditAllocationDialog({
    allocation,
    onSave,
    onCancel,
    open,
    isSaving = false,
    canCreateSharedAllocation = false,
    existingAllocationNames = [],
    existingAllocationGroupNames = [],
    availableStrategies,
}: EditAllocationDialogProps) {
    const nameInputRef = useRef<HTMLInputElement | null>(null);
    const [showAddStrategy, setShowAddStrategy] = useState(false);
    const [selectedStrategyGUID, setSelectedStrategyGUID] = useState<string>('');

    const getDefaultValues = useCallback(
        (allocationData?: any): FormValues => getDefaultFormValues(allocationData),
        [],
    );

    const formSchema = createFormSchema(existingAllocationNames, allocation?.allocationName, availableStrategies);

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(allocation),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(allocation));
    }, [allocation, form, getDefaultValues]);

    useEffect(() => {
        if (open) {
            form.reset(getDefaultValues(allocation));
            const timer = setTimeout(() => {
                nameInputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [open, allocation, form, getDefaultValues]);

    const strategyAllocations = form.watch('strategyAllocations');
    const allocationType = form.watch('allocationType');

    const allocationGroupOptions = useMemo(() => {
        const relevantGroupNames = (existingAllocationGroupNames || [])
            .filter((groupInfo) => groupInfo.allocationType === allocationType)
            .map((groupInfo) => groupInfo.name);
        const uniqueGroupNames = [...new Set(relevantGroupNames)].filter(Boolean).sort();
        return uniqueGroupNames.map((name) => ({ value: name, label: name }));
    }, [existingAllocationGroupNames, allocationType]);

    const { strategyTypeFilter, setStrategyTypeFilter, availableStrategiesToAdd } = useStrategyFilter(
        availableStrategies,
        strategyAllocations,
        allocationType,
    );

    const { handleAddStrategy, removeStrategy, handleUpdateWeight, normalizeDataForSave } =
        useStrategyAllocationManager(form);

    const onAddStrategy = () => {
        if (!selectedStrategyGUID) return;
        handleAddStrategy(selectedStrategyGUID);
        setShowAddStrategy(false);
        setSelectedStrategyGUID('');
    };

    const handleSave = (data: FormValues) => {
        const normalizedData = normalizeDataForSave(data);
        onSave(normalizedData);
    };

    const isBlackrockEnabled = !!useFeatureFlag(FeatureFlag.CC_BLACKROCK_STRATEGIES);

    const handleTypeFilterChange = (type: StrategyTabKey) => {
        setStrategyTypeFilter(type);
        setSelectedStrategyGUID('');
    };

    const totalWeight = calculateTotalWeight(strategyAllocations);
    const isValidTotal = totalWeight === 100;

    const isAdding = !allocation?.allocationGUID;
    const modalTitle = isAdding ? 'New Allocation' : 'Edit Allocation';

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent className="flex h-[85vh] max-w-4xl flex-col bg-white">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuChartPie className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 flex-1 overflow-y-auto px-1">
                    <Form {...form}>
                        <form id="allocation-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                            <div className="space-y-1">
                                <FormInput
                                    control={form.control}
                                    name="allocationName"
                                    label="Allocation name"
                                    placeholder="Enter allocation name"
                                    inputRef={nameInputRef}
                                />
                            </div>

                            <AllocationGroupNameField
                                control={form.control}
                                name="allocationGroupName"
                                allocationGroupOptions={allocationGroupOptions}
                            />

                            <AllocationTypeSelector
                                control={form.control}
                                name="allocationType"
                                disabled={!isAdding || !canCreateSharedAllocation}
                                disabledReason={
                                    !isAdding
                                        ? 'Allocation type cannot be changed after creation.'
                                        : !canCreateSharedAllocation
                                          ? 'Only support users can create shared allocations.'
                                          : undefined
                                }
                            />

                            <div>
                                <div className="flex items-center justify-between">
                                    <FormLabel>Strategies</FormLabel>
                                    <span
                                        className={`text-sm font-medium ${isValidTotal ? 'text-green-600' : 'text-red-600'}`}
                                    >
                                        Total: {totalWeight}%
                                    </span>
                                </div>
                                {form.formState.errors.strategyAllocations && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {form.formState.errors.strategyAllocations.message}
                                    </p>
                                )}

                                <div className="mt-3 space-y-2">
                                    {strategyAllocations.map((strategyAllocation, index) => {
                                        const strategy = getStrategyByGUID(
                                            availableStrategies,
                                            strategyAllocation.strategyGUID,
                                        );
                                        const fieldError = form.formState.errors.strategyAllocations?.[index]?.weight;
                                        return (
                                            <div key={strategyAllocation.strategyGUID}>
                                                <StrategyAllocationItem
                                                    strategy={strategy}
                                                    strategyAllocation={strategyAllocation}
                                                    onUpdateWeight={handleUpdateWeight}
                                                    onRemove={removeStrategy}
                                                    error={fieldError}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {showAddStrategy ? (
                                    <AddStrategySection
                                        availableStrategies={availableStrategiesToAdd}
                                        selectedStrategyGUID={selectedStrategyGUID}
                                        onStrategySelect={setSelectedStrategyGUID}
                                        onAdd={onAddStrategy}
                                        onCancel={() => {
                                            setShowAddStrategy(false);
                                            setSelectedStrategyGUID('');
                                        }}
                                        strategyTypeFilter={strategyTypeFilter}
                                        onTypeFilterChange={handleTypeFilterChange}
                                        allocationType={allocationType}
                                        showBlackrock={isBlackrockEnabled}
                                    />
                                ) : (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowAddStrategy(true)}
                                        className="mt-4 w-full"
                                    >
                                        Add strategy
                                    </Button>
                                )}
                            </div>
                        </form>
                    </Form>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        Cancel
                    </Button>
                    <Button variant="default" type="submit" form="allocation-form" disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
